import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { XCircle, Check, AlertCircle } from 'lucide-react';

const CancelLead = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [error, setError] = useState('');
  const [cancelled, setCancelled] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  useEffect(() => {
    fetchLeadDetails();
  }, [token]);
  
  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/leads/cancel/${token}`
      );
      
      if (response.data.success) {
        setLead(response.data.lead);
      }
    } catch (error) {
      console.error('Fetch lead error:', error);
      setError(error.response?.data?.detail || 'Lead not found or already cancelled');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelLead = async () => {
    if (!window.confirm('Are you sure you want to cancel this job posting? This action cannot be undone.')) {
      return;
    }
    
    setCancelling(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/leads/cancel/${token}`
      );
      
      if (response.data.success) {
        setCancelled(true);
      }
    } catch (error) {
      console.error('Cancel lead error:', error);
      setError(error.response?.data?.detail || 'Failed to cancel lead');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center p-4">
      {/* Header with logo */}
      <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-[#7B3FF2] to-[#9D5CFF] shadow-2xl">
        <div className="max-w-6xl mx-auto px-6 py-6 text-center">
          <img
            src="/assets/shootzu-logo-white.png"
            alt="Shootzu"
            className="h-12 mx-auto"
          />
        </div>
      </div>
      
      {/* Main content */}
      <div className="max-w-2xl w-full mt-24">
        {loading ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-white/30 border-t-white mb-4"></div>
            <p className="text-white">Loading...</p>
          </div>
        ) : error ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 text-center">
            <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Unable to Cancel</h2>
            <p className="text-xl text-gray-300 mb-6">{error}</p>
            <a
              href="https://shootzu.com/getquotes"
              className="inline-block px-8 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all"
            >
              Return to Get Quotes
            </a>
          </div>
        ) : cancelled ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 text-center">
            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-14 h-14 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Job Posting Cancelled</h2>
            <p className="text-xl text-gray-300 mb-6">
              Your job posting has been successfully cancelled. No further matches will be sent.
            </p>
            <div className="bg-white/20 rounded-2xl p-6 mb-6">
              <p className="text-gray-300 mb-2">Request Number:</p>
              <p className="text-2xl font-bold text-white">{lead?.lead_number}</p>
            </div>
            <div className="space-y-4 text-left bg-white/10 rounded-2xl p-6">
              <p className="text-gray-300">
                ✓ Your posting has been removed from the platform
              </p>
              <p className="text-gray-300">
                ✓ No professionals will be able to view or purchase your lead
              </p>
              <p className="text-gray-300">
                ✓ Any professionals who already purchased will still have your details
              </p>
            </div>
            <div className="mt-8">
              <a
                href="https://shootzu.com/getquotes"
                className="inline-block px-8 py-3 bg-gradient-to-r from-[#7B3FF2] to-[#9D5CFF] text-white rounded-xl hover:shadow-lg transition-all font-medium"
              >
                Post Another Request
              </a>
            </div>
          </div>
        ) : lead ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 border border-white/20">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-white" />
              </div>
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-4 text-center">Cancel Job Posting?</h2>
            <p className="text-lg text-gray-300 mb-8 text-center">
              Are you sure you want to cancel this request? This action cannot be undone.
            </p>
            
            <div className="bg-white/20 rounded-2xl p-6 mb-8">
              <h3 className="text-xl font-bold text-white mb-4">Request Details:</h3>
              <div className="space-y-3 text-gray-200">
                <div className="flex justify-between">
                  <span>Request Number:</span>
                  <span className="font-bold text-white">{lead.lead_number}</span>
                </div>
                <div className="flex justify-between">
                  <span>Services:</span>
                  <span className="font-bold text-white">
                    {lead.service_required.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' & ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Date Required:</span>
                  <span className="font-bold text-white">
                    {new Date(lead.date_required).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Location:</span>
                  <span className="font-bold text-white text-right">{lead.location}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-900/30 border border-yellow-600 rounded-xl p-4 mb-8">
              <p className="text-yellow-200 text-sm">
                <strong>⚠️ Warning:</strong> If you cancel this posting, professionals will no longer be able to view or purchase your lead. However, any professionals who have already purchased your details will still have access to your contact information.
              </p>
            </div>
            
            <div className="flex gap-4">
              <a
                href="https://shootzu.com/getquotes"
                className="flex-1 py-4 bg-white/10 text-white text-center rounded-xl font-medium hover:bg-white/20 transition-all"
              >
                Keep Active
              </a>
              <button
                onClick={handleCancelLead}
                disabled={cancelling}
                className="flex-1 py-4 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel Posting'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
      
      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/30 py-6">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">
            © 2025 Shootzu. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CancelLead;
